import { EventProperties } from '@segment/analytics-next'
import { useEffect, useState } from 'react'
import { useSellerInfo } from '@/proxy/seller/hooks'
import { SellerLocation } from '@/resources/types/seller'
import useFeatureToggle from '@/hooks/useFeatureToggle'
import { shouldActivateAnalytics } from '@/contexts/analytics'
import { FeatureToggle } from '@/resources/enums/featureToggle'
import { useRouterStore } from '@/contexts/router'
import { STORE_OPEN_STATUS } from '@/resources/utils'
import { client, sendAnalyticsEvent } from '@/analytics/segmentClient'

type useAnalyticsProps = {
  (): <T extends EventProperties>(
    eventName: string,
    properties?: T,
    forceEvent?: boolean
  ) => void
}

const prepareSellerData = (
  seller: SellerLocation,
  isSellerInfoFeched: boolean
) => {
  if (!seller || !isSellerInfoFeched) {
    return null
  }

  const {
    id: pocId = '',
    address = {
      provinceCode: '',
      city: '',
      location: {
        coordinates: [0, 0],
      },
    },
  } = seller

  const {
    provinceCode: uf,
    city,
    location: { coordinates },
  } = address

  const store_is_open = localStorage.getItem(STORE_OPEN_STATUS) === 'true'
  const poc_id = pocId.toString()

  return {
    poc_id,
    uf,
    city,
    coordinates,
    store_is_open,
  }
}

export const useAnalytics: useAnalyticsProps = () => {
  const analyticsEnableToggle = useFeatureToggle({
    splitName: 'enable_segment_analytics',
    includeLoadingState: true,
  })

  const [isAnalyticsEnabled, setIsAnalyticsEnabled] = useState(false)

  const { data: { seller } = {}, isFetched: isSellerInfoFeched } =
    useSellerInfo()

  const {
    previousPage: previous_screen_name = null,
    currentPage: screen_name = null,
  } = useRouterStore()

  useEffect(() => {
    setIsAnalyticsEnabled(analyticsEnableToggle === FeatureToggle.ON)
    if (analyticsEnableToggle === FeatureToggle.OFF) {
      console.error(
        'Analytics is disabled! Check the feature toggle "enable_segment_analytics"'
      )
    }
  }, [analyticsEnableToggle])

  useEffect(() => {
    if (!client && shouldActivateAnalytics) {
      console.error(
        'Context used outside of its Provider or Segment writeKey not provided!'
      )
    }
  }, [])

  return async <T extends EventProperties>(
    eventName: string,
    eventProperties: T = {} as T,
    forceEvent = false
  ) => {
    if ((isAnalyticsEnabled || forceEvent) && client) {
      const sellerData = prepareSellerData(seller, isSellerInfoFeched)
      if (!sellerData) return

      const sendEvent = () =>
        sendAnalyticsEvent(eventName, {
          device_id: client.instance?.user()?.anonymousId(),
          screen_name,
          previous_screen_name,
          previous_component_name: null,
          ...sellerData,
          ...eventProperties,
        })

      await client.ready(sendEvent)
    }
  }
}
